import { Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { formatMoney } from "utils/functions";
import { CreditorMonthlyWithhold } from "./APRCharts";

type TableRow = {
  key: string;
  label: string;
  monthly_revenue: number | null; // null on child rows (revenue is shared, not per-creditor)
  monthly_payment_burden: number;
  withhold_percentage: number;
  isParent: boolean;
  children?: TableRow[];
};

function buildRows(data: CreditorMonthlyWithhold[]): TableRow[] {
  const monthMap = new Map<string, { totalRevenue: number; totalBurden: number; children: TableRow[] }>();

  for (const creditor of data) {
    for (const m of creditor.monthly_breakdown) {
      if (!monthMap.has(m.month)) {
        monthMap.set(m.month, { totalRevenue: 0, totalBurden: 0, children: [] });
      }
      const entry = monthMap.get(m.month)!;
      entry.totalRevenue += m.monthly_revenue; // ← sum, not assign
      entry.totalBurden += m.monthly_payment_burden;
      entry.children.push({
        key: `${m.month}__${creditor.creditor_name}`,
        label: creditor.creditor_name,
        monthly_revenue: m.monthly_revenue, // ← per-creditor value, not null
        monthly_payment_burden: m.monthly_payment_burden,
        withhold_percentage: m.withhold_percentage,
        isParent: false,
      });
    }
  }

  return Array.from(monthMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, { totalRevenue, totalBurden, children }]) => ({
      key: month,
      label: month,
      monthly_revenue: totalRevenue, // ← summed across all creditors
      monthly_payment_burden: totalBurden,
      withhold_percentage: children.reduce((sum, c) => sum + c.withhold_percentage, 0),
      isParent: true,
      children,
    }));
}

function withholdColor(pct: number): string {
  if (pct >= 40) return "red";
  if (pct >= 10) return "orange";
  if (pct > 0) return "green";
  return "default";
}

const columns: ColumnsType<TableRow> = [
  {
    title: "Month / Creditor",
    dataIndex: "label",
    key: "label",
    width: "30%",
    render: (v: string, record) =>
      record.isParent ? (
        <span className="text-[13px] font-semibold" style={{ color: "#18181B" }}>
          {v}
        </span>
      ) : (
        <span className="text-[13px]" style={{ color: "#52525B" }}>
          {v}
        </span>
      ),
  },
  {
    title: "Monthly Revenue",
    dataIndex: "monthly_revenue",
    key: "monthly_revenue",
    align: "right",
    render: (v: number, record) => (
      <span
        className="text-[13px]"
        style={{
          color: "#18181B",
          fontWeight: record.isParent ? 600 : 400,
        }}
      >
        {record.isParent ? formatMoney(v) : "-"}
      </span>
    ),
  },
  {
    title: "Payment Burden",
    dataIndex: "monthly_payment_burden",
    key: "monthly_payment_burden",
    align: "right",
    render: (v: number, record) => {
      if (v === 0) {
        return <span style={{ color: "#A1A1AA", fontSize: 12 }}>0</span>;
      }
      return (
        <span
          className="text-[13px]"
          style={{ color: record.isParent ? "#18181B" : "#52525B", fontWeight: record.isParent ? 600 : 400 }}
        >
          {formatMoney(v)}
        </span>
      );
    },
  },
  {
    title: "Withhold %",
    dataIndex: "withhold_percentage",
    key: "withhold_percentage",
    align: "right",
    width: 120,
    render: (v: number) => {
      if (v === 0) {
        return (
          <Typography.Text
            color={withholdColor(0)}
            style={{ margin: 0, textAlign: "center", fontSize: 12, minWidth: 56, fontWeight: 500 }}
          >
            0%
          </Typography.Text>
        );
      }
      return (
        <Tag color={withholdColor(v)} style={{ minWidth: 56, textAlign: "center", fontSize: 12, fontWeight: 500 }}>
          {v.toFixed(2)}%
        </Tag>
      );
    },
  },
];

type Props = { data: CreditorMonthlyWithhold[] };

export function MonthlyBreakdownTable({ data }: Props) {
  const rows = buildRows(data);

  return (
    <Table<TableRow>
      columns={columns}
      dataSource={rows}
      pagination={false}
      size="small"
      rowClassName={(record) => (record.isParent ? "bg-zinc-50" : "bg-white")}
      indentSize={20}
    />
  );
}
